import { clamp, lerp } from "@/utils/math"
import { Adjustments, MaskGroupAdjustments, PointColorHSL } from "./types"
import { RGBToHSV } from "./utils/color-conversion"
import {
	getHSVRelativeShiftInRange,
	getLumShiftRange,
	getSatShiftRange,
} from "@/components/HSL/utils"
import { vec4 } from "gl-matrix"

export const HSLPointColorMaxCount = 10
export const PerPointColorSize = 16
export const HSLPointColorDataSize = HSLPointColorMaxCount * PerPointColorSize
export const HSLPointColorBufferSize = HSLPointColorDataSize * 4

// Todo: ideally we should seperate maxShift and maxRange; but using the same number works roughly ok
export const PointColorHueMaxShift = 50 // maxHueShift = 35 in shader; but different color space, and 50 works ok for display
export const PointColorSatMaxShift = 60
export const PointColorLumMaxShift = 50

export function writeHSLPointColorData(
	values: Adjustments | MaskGroupAdjustments,
	bufferData: Float32Array,
	offset: number,
) {
	if (!values.pointColors || values.pointColors.length === 0) {
		return
	}

	const count = Math.min(values.pointColors.length, HSLPointColorMaxCount)
	for (let i = 0; i < count; i++) {
		const adjustments = values.pointColors[i] as PointColorHSL
		const strength = values["strength"] ?? 1.0
		const baseIndex = offset + i * PerPointColorSize

		if (baseIndex + 15 >= bufferData.length) break // total local pointColors num exceeds max count (50)

		if (!adjustments) {
			// bad value from legacy preset etc.
			bufferData.fill(0, baseIndex, baseIndex + PerPointColorSize)
			continue
		}

		const hsv = RGBToHSV(adjustments.color)
		const relativeHSV = getHSVRelativeShiftInRange(hsv)

		const hueRange = ilerpHueRange(
			adjustments.hueRange,
			hsv[0] / 360,
			PointColorHueMaxShift / 360,
			adjustments.range,
		)

		// relax upper limit for sat from 1.0 to 1.1 to include full saturated color
		const satNormRange: vec4 =
			adjustments.satRange == undefined
				? [0.0, 0.33, 0.67, 1.0]
				: (adjustments.satRange.slice() as vec4)
		if (satNormRange[3] === 1)
			satNormRange[3] = clamp((hsv[1] + PointColorSatMaxShift) / 100, 1, 1.1)
		const satRange = ilerpSatLumRange(
			satNormRange,
			relativeHSV[1],
			adjustments.range,
			getSatShiftRange(hsv),
		)

		const lumNormRange: vec4 =
			adjustments.lumRange == undefined
				? [0.0, 0.33, 0.67, 1.0]
				: (adjustments.lumRange.slice() as vec4)
		if (lumNormRange[3] === 1)
			lumNormRange[3] = clamp((hsv[2] + PointColorLumMaxShift) / 100, 1, 1.1)
		const lumRange = ilerpSatLumRange(
			lumNormRange,
			relativeHSV[2],
			adjustments.range,
			getLumShiftRange(hsv),
		)

		bufferData[baseIndex + 0] = adjustments.hueShift * strength
		bufferData[baseIndex + 1] = adjustments.satShift * strength
		bufferData[baseIndex + 2] = adjustments.lumShift * strength

		bufferData[baseIndex + 3] = adjustments.range

		bufferData[baseIndex + 4] = hueRange[0]
		bufferData[baseIndex + 5] = hueRange[1]
		bufferData[baseIndex + 6] = hueRange[2]
		bufferData[baseIndex + 7] = hueRange[3]

		bufferData[baseIndex + 8] = lumRange[0]
		bufferData[baseIndex + 9] = lumRange[1]
		bufferData[baseIndex + 10] = lumRange[2]
		bufferData[baseIndex + 11] = lumRange[3]

		bufferData[baseIndex + 12] = satRange[0]
		bufferData[baseIndex + 13] = satRange[1]
		bufferData[baseIndex + 14] = satRange[2]
		bufferData[baseIndex + 15] = satRange[3]
	}
}

function ilerpSatLumRange(
	normalizedRange: [number, number, number, number],
	relativeMiddle: number,
	rangeParam: number,
	absoluteBounds: [number, number],
): [number, number, number, number] {
	if (typeof normalizedRange === "undefined") {
		normalizedRange = [0, 0.33, 0.67, 1]
	}

	const adjustedRange = normalizedRange.slice() as [number, number, number, number]

	adjustedRange[1] = Math.max(adjustedRange[0], adjustedRange[1])
	adjustedRange[2] = Math.max(adjustedRange[1], adjustedRange[2])
	adjustedRange[3] = Math.max(adjustedRange[2], adjustedRange[3])

	const factor = lerp(0.6, 1.4, rangeParam)
	adjustedRange.forEach((val, index, arr) => {
		arr[index] = (val - relativeMiddle) * factor + relativeMiddle
	})

	return adjustedRange.map((val) => lerp(absoluteBounds[0], absoluteBounds[1], val) / 100) as [
		number,
		number,
		number,
		number,
	]
}

function ilerpHueRange(
	normalizedRange: [number, number, number, number],
	absoluteMiddle: number,
	amplitude: number,
	rangeParam: number,
): [number, number, number, number] {
	const relativeMiddle = 0.5

	if (typeof normalizedRange === "undefined") {
		normalizedRange = [0, 0.33, 0.67, 1]
	}

	const makeCircular = (value: number): number => {
		let circularValue = value % 1
		if (circularValue < 0) {
			circularValue += 1
		}
		return circularValue
	}

	const shiftAndWrap = (value: number): number => {
		const shiftedValue = absoluteMiddle + (value - relativeMiddle) * amplitude * 2
		return makeCircular(shiftedValue)
	}

	const adjustedRange = normalizedRange.slice() as [number, number, number, number]

	adjustedRange[1] = Math.max(adjustedRange[0], adjustedRange[1])
	adjustedRange[2] = Math.max(adjustedRange[1], adjustedRange[2])
	adjustedRange[3] = Math.max(adjustedRange[2], adjustedRange[3])

	const factor = lerp(0.75, 1.25, rangeParam)
	const factorOuter = Math.min(factor, 1.0) // don't enlarge the hard range of hue

	adjustedRange[0] = (adjustedRange[0] - relativeMiddle) * factorOuter + relativeMiddle
	adjustedRange[1] = Math.max(
		(adjustedRange[1] - relativeMiddle) * factor + relativeMiddle,
		adjustedRange[0],
	)

	adjustedRange[3] = (adjustedRange[3] - relativeMiddle) * factorOuter + relativeMiddle
	adjustedRange[2] = Math.min(
		(adjustedRange[2] - relativeMiddle) * factor + relativeMiddle,
		adjustedRange[3],
	)

	const absoluteHardStart = shiftAndWrap(adjustedRange[0])
	const absoluteSoftStart = shiftAndWrap(adjustedRange[1])
	const absoluteSoftEnd = shiftAndWrap(adjustedRange[2])
	const absoluteHardEnd = shiftAndWrap(adjustedRange[3])

	return [absoluteHardStart, absoluteSoftStart, absoluteSoftEnd, absoluteHardEnd]
}
